import OpenAI from "openai";
import { contentPrompt } from "../prompts/contentPrompt";
import { pagePrompt } from "../prompts/pagePrompt";

let cachedClient = null;

// --------------------
// Client factory
// --------------------
const getOpenAIClient = () => {
  if (cachedClient) return cachedClient;

  const apiKey =
    process.env.REACT_APP_OPENAI_API_KEY || process.env.OPENAI_API_KEY;

  if (!apiKey) {
    throw new Error(
      "❌ Missing OpenAI API key. Please set REACT_APP_OPENAI_API_KEY in your .env.local file."
    );
  }

  cachedClient = new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true, // ✅ allowed for MVP testing
  });

  return cachedClient;
};

// --------------------
// Safe JSON helpers
// --------------------
const extractJsonStringFromContent = (content) => {
  if (!content || typeof content !== "string") return "";
  const startArray = content.indexOf("[");
  const startObject = content.indexOf("{");

  let candidate = content;
  if (startArray !== -1 && (startObject === -1 || startArray < startObject)) {
    const endArray = content.lastIndexOf("]");
    if (endArray !== -1 && endArray > startArray) {
      candidate = content.slice(startArray, endArray + 1);
    }
  } else if (startObject !== -1) {
    const endObject = content.lastIndexOf("}");
    if (endObject !== -1 && endObject > startObject) {
      candidate = content.slice(startObject, endObject + 1);
    }
  }
  return candidate
    .replace(/^```(json)?/i, "")
    .replace(/```$/i, "")
    .trim();
};

const safeParse = (text, fallback) => {
  try {
    return JSON.parse(text);
  } catch {
    try {
      return JSON.parse(extractJsonStringFromContent(text));
    } catch {
      return fallback;
    }
  }
};

// --------------------
// Phase-1: Content
// --------------------
export const generateContent = async (appName, appDesc) => {
  // ⚠️ COMMENTED OUT - OpenAI API calls disabled to prevent quota exhaustion
  // Will be re-enabled when prompts are standardized
  
  /* ORIGINAL CODE - COMMENTED OUT
  const client = getOpenAIClient();
  const prompt = contentPrompt(appName, appDesc);

  console.debug("📨 Phase-1 Prompt:", prompt);

  const response = await client.chat.completions.create({
    model: "gpt-4",
    messages: [{ role: "user", content: prompt }],
    temperature: 0.2,
    max_tokens: 1800,
  });

  const text = response.choices?.[0]?.message?.content?.trim() || "{}";
  console.info("📥 Phase-1 Raw:", text);

  const json = safeParse(text, { pages: [], tagline: "", globalCTA: "" });
  console.debug("✅ Phase-1 Parsed:", json);

  return json;
  */

  // TEMPORARY MOCK RESPONSE
  console.warn("⚠️ OpenAI API disabled - returning mock content");
  return {
    pages: ["Home", "About", "Services", "Contact", "Blog"],
    tagline: "Welcome to " + appName,
    globalCTA: "Get Started"
  };
};

// --------------------
// Phase-2: Pages / Components / Layouts / Theme
// --------------------
export const generateAppJSON = async (appName, appDesc, contentJSON) => {
  // ⚠️ COMMENTED OUT - OpenAI API calls disabled to prevent quota exhaustion
  // Will be re-enabled when prompts are standardized
  
  /* ORIGINAL CODE - COMMENTED OUT
  const client = getOpenAIClient();
  const prompt = pagePrompt(appName, appDesc, contentJSON);

  console.debug("📨 Phase-2 Prompt:", prompt);

  const response = await client.chat.completions.create({
    model: "gpt-4",
    messages: [{ role: "user", content: prompt }],
    temperature: 0.2,
    max_tokens: 3500,
  });

  const text = response.choices?.[0]?.message?.content?.trim() || "[]";
  console.info("📥 Phase-2 Raw:", text);

  const json = safeParse(text, []);
  console.debug("✅ Phase-2 Parsed:", json);

  return json;
  */

  // TEMPORARY MOCK RESPONSE
  console.warn("⚠️ OpenAI API disabled - returning empty array");
  return [];
};

// --------------------
// Sample Data Generation for Data Management
// --------------------
/**
 * Generate sample data for a data table using OpenAI
 * @param {string} tableName - Name of the table
 * @param {Array} fields - Field definitions [{name, type, required}]
 * @param {number} recordCount - Number of sample records to generate (default: 15)
 * @returns {Promise<Array>} Array of sample records
 */
export const generateSampleData = async (tableName, fields, recordCount = 15) => {
  try {
    const client = getOpenAIClient();
    
    // Build field descriptions for the prompt
    const fieldDescriptions = fields
      .map(f => `- ${f.name} (${f.type}${f.required ? ', required' : ''})`)
      .join('\n');
    
    const prompt = `Generate ${recordCount} realistic sample records for a data table named "${tableName}".

Fields:
${fieldDescriptions}

Requirements:
1. Return ONLY a valid JSON array of objects
2. Each object should have all the fields listed above
3. Use realistic, diverse data that makes sense for the field names and types
4. For dates, use ISO 8601 format (YYYY-MM-DD)
5. For booleans, use true/false
6. For numbers, use appropriate numeric values
7. For emails, use valid email formats
8. Do not include any markdown, code blocks, or explanations
9. Do not include system fields like id, createdAt, createdBy, updatedAt, updatedBy

Example format:
[
  {"field1": "value1", "field2": 123, "field3": true},
  {"field1": "value2", "field2": 456, "field3": false}
]`;

    console.debug("📨 Sample Data Generation Prompt:", prompt);

    const response = await client.chat.completions.create({
      model: "gpt-4",
      messages: [{ role: "user", content: prompt }],
      temperature: 0.8,
      max_tokens: 2000,
    });

    const text = response.choices?.[0]?.message?.content?.trim() || "[]";
    console.info("📥 Sample Data Raw Response:", text);

    // Parse the JSON response
    const sampleData = safeParse(text, []);
    console.debug("✅ Sample Data Parsed:", sampleData);

    return Array.isArray(sampleData) ? sampleData : [];
  } catch (error) {
    console.error("❌ Sample data generation failed:", error);
    // Return empty array on error
    return [];
  }
};
